import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Button,
  Col,
  Form,
  Row,
  Table,
  Container,
  Card,
  Modal,
  Badge,
  ListGroup,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { ShimmerTable } from "react-shimmer-effects";
import WhatsAppAPI from "../../api/WhatsAppAPI";
import { isMobile } from "react-device-detect";
import moment from "moment";
import {
  DatatableWrapper,
  Filter,
  Pagination,
  PaginationOptions,
  TableBody,
  TableHeader,
} from "react-bs-datatable";
import { Link } from "react-router-dom";
import ImportLeads from "./ImportLeads";
import {
  FaPlus,
  FaTags,
  FaUpload,
  FaTimes,
  FaSearch,
  FaFilter,
  FaTag,
  FaUsers,
  FaUserPlus,
  FaUserFriends,
  FaUserMinus,
  FaUserCheck,
  FaThumbtack,
  FaBan,
} from "react-icons/fa";
import { FaXmark } from "react-icons/fa6";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import jwt_decode from "jwt-decode";
import Select from "react-select";
import Confirm from "../Confirm";

// Context so select-all checkbox re-renders when selection changes (header array stays stable for pagination)
const LeadListSelectionContext = React.createContext({
  selectedRows: [],
  body: [],
  setSelectedRows: () => {},
});

// Uses context so it re-renders when selection changes; uncheck works correctly
function SelectAllCheckbox() {
  const { selectedRows, body, setSelectedRows } = React.useContext(
    LeadListSelectionContext,
  );
  const allSelected = body.length > 0 && selectedRows.length === body.length;
  return (
    <Form.Check
      type="checkbox"
      id="select-all-checkbox"
      checked={allSelected}
      onChange={(e) => {
        setSelectedRows(e.target.checked ? body.map((row) => row.id) : []);
      }}
    />
  );
}

const LeadList = () => {
  const navigate = useNavigate();
  const [body, setBody] = useState([]);
  const [leadname, setLeadName] = useState();
  const [lead, setLead] = useState();
  const [leadStatusArray, setleadStatusArray] = useState(
    JSON.parse(sessionStorage.getItem("lead_status")),
  );
  const [isSpinner, setIsSpinner] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [user, setUser] = useState(null);
  const [pinLoading, setPinLoading] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [userInfo, setUserInfo] = useState(
    jwt_decode(sessionStorage.getItem("token")),
  );

  // New state for assign agents modal
  const [showAssignAgentsModal, setShowAssignAgentsModal] = useState(false);
  const [availableAgents, setAvailableAgents] = useState([]);
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [assignMode, setAssignMode] = useState("replace"); // 'replace' or 'add'
  const [assignLoading, setAssignLoading] = useState(false);

  // New state variables for multi-tag search
  const [selectedTags, setSelectedTags] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // State for agent filter
  const [selectedAgentFilter, setSelectedAgentFilter] = useState([]);

  // Refs to keep header reference stable (prevents DatatableWrapper from resetting pagination on checkbox change)
  const selectedRowsRef = useRef(selectedRows);
  const bodyRef = useRef(body);
  selectedRowsRef.current = selectedRows;
  bodyRef.current = body;

  const selectionContextValue = useMemo(
    () => ({ selectedRows, body, setSelectedRows }),
    [selectedRows, body],
  );

  const formatPhoneNumber = (number) => {
    const shouldHideNumber =
      userInfo?.userrole !== "ADMIN" &&
      userInfo?.is_whatsapp_number_hidden === true;

    if (shouldHideNumber && number) {
      const numberString = String(number);
      if (numberString.length <= 5) return numberString;
      const visibleDigits = numberString.slice(-5);
      const maskedDigits = "X".repeat(numberString.length - 5);
      return `${maskedDigits}${visibleDigits}`;
    }
    return number;
  };

  const formatDate = (date) => {
    return moment(date).format("DD MMM YYYY");
  };

  const init = async () => {
    try {
      const userData = await WhatsAppAPI.getLoginUserData();
      setUser(userData);
      const leads = await WhatsAppAPI.fetchLead();

      if (leads && leads.success) {
        setBody(leads?.records);
        setLead(leads?.records);
        // Extract unique tags from all leads
        const allTags = new Map();
        leads.records.forEach((lead) => {
          if (lead.tag_names && Array.isArray(lead.tag_names)) {
            lead.tag_names.forEach((tag) => {
              if (tag && tag.name) {
                allTags.set(tag.name, tag);
              }
            });
          }
        });

        // Convert to array of objects for react-select
        const tagOptions = Array.from(allTags.values()).map((tag) => ({
          value: tag.name,
          label: tag.name,
          color: tag.color || "#6c757d",
        }));
        setAvailableTags(tagOptions);

        // Also fetch agents for filter
        const fetchUser = await WhatsAppAPI.fetchUsers();
        const formattedAgents = fetchUser.map((item) => ({
          value: item.id,
          label: item.username,
          email: item.email || "",
          full_name: item.full_name || "",
          ...item,
        }));
        setAvailableAgents(formattedAgents);
      } else {
        console.error(
          "Failed to fetch leads:",
          leads?.message || "Unknown error",
        );
        setBody([]);
        setLead([]);
        setAvailableTags([]);
        toast.error("Failed to load leads. Please try again.");
      }
      setIsSpinner(true);
    } catch (error) {
      console.error("Error in init function:", error);
      setBody([]);
      setLead([]);
      setAvailableTags([]);
      setIsSpinner(true);
      toast.error("Error loading leads. Please refresh the page.");
    }
  };

  const fetchAgents = async () => {
    try {
      const fetchUser = await WhatsAppAPI.fetchUsers();
      const formattedAgents = fetchUser.map((item) => ({
        value: item.id,
        label: item.username,
        email: item.email || "",
        full_name: item.full_name || "",
        ...item,
      }));
      setAvailableAgents(formattedAgents);
    } catch (error) {
      console.error("Error fetching agents:", error);
      toast.error("Failed to load agents");
    }
  };

  useEffect(() => {
    init();
  }, []);

  // Update selectedLeads when selectedRows changes
  useEffect(() => {
    if (selectedRows.length > 0 && body.length > 0) {
      const selectedLeadDetails = body.filter((lead) =>
        selectedRows.includes(lead.id),
      );
      setSelectedLeads(selectedLeadDetails);
    } else {
      setSelectedLeads([]);
    }
  }, [selectedRows, body]);

  // Function to check if lead has specific agents (OR logic)
  const leadHasAgents = (lead, agentIds) => {
    if (!agentIds || agentIds.length === 0) return true;

    const leadAgentIds = lead.all_owner_names?.map((owner) => owner.id) || [];
    return agentIds.some((agentId) => leadAgentIds.includes(agentId));
  };

  // Combined filter function
  const applyFilters = () => {
    if (!lead || lead.length === 0) return;

    let filteredLeads = [...lead];

    // Apply search query filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filteredLeads = filteredLeads.filter((leadItem) => {
        const hasAgentMatch = leadItem.all_owner_names?.some(
          (owner) =>
            owner.name?.toLowerCase().includes(query) ||
            owner.email?.toLowerCase().includes(query),
        );

        return (
          leadItem.leadname?.toLowerCase().includes(query) ||
          leadItem.email?.toLowerCase().includes(query) ||
          leadItem.whatsapp_number?.toString().includes(searchQuery) ||
          leadItem.leadstatus?.toLowerCase().includes(query) ||
          leadItem.country_code?.toString().includes(searchQuery) ||
          hasAgentMatch
        );
      });
    }

    // Apply status filter
    if (statusFilter) {
      filteredLeads = filteredLeads.filter(
        (leadItem) =>
          leadItem.leadstatus?.toLowerCase() === statusFilter.toLowerCase(),
      );
    }

    // Apply tag filter (OR logic - lead must have at least one selected tag)
    if (selectedTags.length > 0) {
      filteredLeads = filteredLeads.filter((leadItem) => {
        const leadTags = leadItem.tag_names || [];
        const leadTagNames = leadTags.map((tag) => tag.name);

        // Check if lead has at least one of the selected tags
        return selectedTags.some((selectedTag) =>
          leadTagNames.includes(selectedTag.value),
        );
      });
    }

    // Apply agent filter (OR logic - lead must have at least one selected agent)
    if (selectedAgentFilter.length > 0) {
      const selectedAgentIds = selectedAgentFilter.map((agent) => agent.value);
      filteredLeads = filteredLeads.filter((leadItem) =>
        leadHasAgents(leadItem, selectedAgentIds),
      );
    }

    setBody(filteredLeads);
  };

  useEffect(() => {
    if (lead && lead.length > 0) {
      applyFilters();
    }
  }, [searchQuery, statusFilter, selectedTags, selectedAgentFilter, lead]);

  const onFilterType = (event) => {
    setStatusFilter(event.target.value);
  };

  const getStatusClass = (status) => {
    let accessStatusRec = leadStatusArray.filter((value, index, array) => {
      if (value.label === status) {
        return true;
      }
    });

    if (accessStatusRec && accessStatusRec.length > 0) {
      if (accessStatusRec[0].is_converted === true) {
        return "success";
      } else if (accessStatusRec[0].is_lost === true) {
        return "secondary";
      } else {
        return "primary";
      }
    } else {
      return "secondary";
    }
  };

  const editLead = (lead) => {
    navigate(`/leads/${lead.id}/e`, { state: lead });
  };

  const handlePinToggle = async (lead) => {
    if (!user || pinLoading[lead.id]) return;
    setPinLoading((prev) => ({ ...prev, [lead.id]: true }));
    if (lead.pinned) {
      await WhatsAppAPI.unpinLead(lead.id);
    } else {
      await WhatsAppAPI.pinLead(lead.id);
    }
    await init();
    setPinLoading((prev) => ({ ...prev, [lead.id]: false }));
  };

  const handleAssignAgentsClick = async () => {
    await fetchAgents();
    setShowAssignAgentsModal(true);
  };

  const handleAssignAgents = async () => {
    if (selectedAgents.length === 0) {
      toast.error("Please select at least one agent");
      return;
    }

    if (selectedRows.length === 0) {
      toast.error("No leads selected");
      return;
    }

    setAssignLoading(true);
    try {
      const agentIds = selectedAgents.map((agent) => agent.value);
      const leadIds = selectedRows;

      // Prepare data for API call
      const assignData = {
        leadIds: leadIds,
        agentIds: agentIds,
        assignMode: assignMode, // 'replace' or 'add'
      };

      // console.log("assi", assignData);
      // Call API to assign agents to selected leads
      const response = await WhatsAppAPI.assignAgentsToLeads(assignData);

      if (response.success) {
        toast.success(
          `Successfully assigned agents to ${leadIds.length} lead(s)`,
        );
        // Refresh the data
        await init();
        // Clear selections
        setSelectedRows([]);
        setSelectedLeads([]);
        setSelectedAgents([]);
        // Close modal
        setShowAssignAgentsModal(false);
      } else {
        toast.error(response.message || "Failed to assign agents");
      }
    } catch (error) {
      console.error("Error assigning agents:", error);
      toast.error("Failed to assign agents");
    } finally {
      setAssignLoading(false);
    }
  };

  const removeSelectedLead = (leadId) => {
    setSelectedRows((prev) => prev.filter((id) => id !== leadId));
  };

  const handleCloseAssignModal = () => {
    setShowAssignAgentsModal(false);
    setSelectedAgents([]);
    setAssignMode("replace");
  };

  const clearAllSelections = () => {
    setSelectedRows([]);
    setSelectedLeads([]);
  };

  const header = useMemo(() => {
    const cols = [];
    if (!isMobile) {
      cols.push(
        {
          title: <SelectAllCheckbox />,
          cell: (row) => (
            <Form.Check
              type="checkbox"
              id={`row-checkbox-${row.id}`}
              checked={selectedRowsRef.current.includes(row.id)}
              onChange={(e) => {
                const checked = e.target.checked;
                setSelectedRows((prev) =>
                  checked
                    ? [...prev, row.id]
                    : prev.filter((id) => id !== row.id),
                );
              }}
            />
          ),
          prop: "select",
        },

        {
          title: "Pin",
          prop: "pin",
          cell: (row) => {
            const isOwner = user && String(row.createdbyid) === String(user.id);
            return (
              <Button
                variant="link"
                className="p-0 m-0"
                style={{ color: row.pinned ? "#007bff" : "#aaa" }}
                onClick={() => isOwner && handlePinToggle(row)}
                disabled={!isOwner || pinLoading[row.id]}
                title={row.pinned ? "Unpin" : "Pin"}
              >
                <FaThumbtack style={row.pinned ? {} : { opacity: 0.5 }} />
              </Button>
            );
          },
        },
        {
          title: "S.No",
          prop: "lead_no",
          isFilterable: true,
        },
        {
          title: "Name",
          prop: "leadname",
          isFilterable: true,
          cell: (row) => (
            <Link
              className="text-capitalize"
              to={"/leads/" + row.id}
              state={row}
            >
              {row.leadname}
            </Link>
          ),
        },
        {
          title: "WhatsApp",
          prop: "whatsapp_number",
          isFilterable: true,
          cell: (row) => (
            <span>{`${row.country_code} ${
              row.display_whatsapp_number ||
              formatPhoneNumber(row.whatsapp_number)
            }`}</span>
          ),
        },
        { title: "Status", prop: "leadstatus", isFilterable: true },
        {
          title: "Assigned To",
          prop: "all_owner_names",
          cell: (row) => {
            const owners = row.all_owner_names || [];
            const visibleOwners = owners.slice(0, 2);
            const remainingCount = owners.length - 2;

            return (
              <div
                style={{
                  maxWidth: "320px",
                  display: "flex",
                  flexWrap: "nowrap",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  gap: "6px",
                  alignItems: "center",
                }}
              >
                {visibleOwners.map((owner, index) => (
                  <span
                    key={index}
                    className="badge bg-info text-white border"
                    style={{
                      borderRadius: "0.375rem",
                      padding: "4px 8px",
                      fontSize: "0.75rem",
                      fontWeight: "500",
                      backgroundColor: "#0dcaf0",
                      border: "1px solid #0dcaf0",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {owner.name} <FaUsers size={12} className="ms-2" />
                  </span>
                ))}
                {remainingCount > 0 && (
                  <span
                    className="badge bg-light text-dark border"
                    style={{
                      borderRadius: "0.375rem",
                      padding: "4px 8px",
                      fontSize: "0.75rem",
                      fontWeight: "500",
                      backgroundColor: "#f0f0f0",
                      border: "1px solid #ccc",
                    }}
                  >
                    +{remainingCount} more
                  </span>
                )}
              </div>
            );
          },
        },
        {
          title: "Tags",
          prop: "tag_names",
          cell: (row) => {
            const tags = row.tag_names || [];
            const visibleTags = tags.slice(0, 2);
            const remainingCount = tags.length - 2;

            return (
              <div
                style={{
                  maxWidth: "320px",
                  display: "flex",
                  flexWrap: "nowrap",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  gap: "6px",
                  alignItems: "center",
                }}
              >
                {visibleTags.map((tag, index) => (
                  <span
                    key={index}
                    className="badge bg-light text-dark border"
                    style={{
                      borderRadius: "0.375rem",
                      padding: "4px 8px",
                      fontSize: "0.75rem",
                      fontWeight: "500",
                      backgroundColor: tag.color || "#f0f0f0",
                      border: "1px solid #ccc",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {tag.name} <FaTags size={14} className="ms-2" />
                  </span>
                ))}
                {remainingCount > 0 && (
                  <span
                    className="badge bg-light text-dark border"
                    style={{
                      borderRadius: "0.375rem",
                      padding: "4px 8px",
                      fontSize: "0.75rem",
                      fontWeight: "500",
                      backgroundColor: "#f0f0f0",
                      border: "1px solid #ccc",
                    }}
                  >
                    +{remainingCount} more
                  </span>
                )}
              </div>
            );
          },
        },

        {
          title: "Created At",
          prop: "createddate",
          isFilterable: true,
          cell: (row) => formatDate(row.createddate),
        },
        {
          title: "Actions",
          prop: "actions",
          cell: (row) =>
            !(
              userInfo?.userrole !== "ADMIN" &&
              userInfo?.is_whatsapp_number_hidden === true
            ) && (
              <Button className="btn-sm mx-2" onClick={() => editLead(row)}>
                <i className="fa-regular fa-pen-to-square" title="Edit"></i>
              </Button>
            ),
        },
      );
    } else {
      header.push({
        title: "Info",
        prop: "leadname",
        isFilterable: true,
        cell: (row) => (
          <div className="mobilecard">
            <Link
              to={"/leads/" + row.id}
              state={row}
              style={{ fontSize: "1.2rem" }}
            >
              {row.leadname}
            </Link>
            <div className="mb-2">
              <i className="fa-solid fa-users me-2"></i>
              {row.all_owner_names && row.all_owner_names.length > 0 ? (
                row.all_owner_names.map((owner, idx) => (
                  <Badge key={idx} bg="info" className="me-1">
                    {owner.name}
                  </Badge>
                ))
              ) : (
                <span className="text-muted">No owners assigned</span>
              )}
            </div>

            <span>
              <i className="fa-solid fa-phone"></i>{" "}
              {row.display_whatsapp_number ||
                formatPhoneNumber(row.whatsapp_number)}
            </span>
            <span style={{ width: "80%" }}>
              <i className="fa-solid fa-envelope"></i> {row.email}
            </span>
            <Badge
              bg={getStatusClass(row.leadstatus)}
              style={{ paddingBottom: "5px", width: "80%" }}
            >
              {row.leadstatus}
            </Badge>
            {!(
              userInfo?.userrole !== "ADMIN" &&
              userInfo?.is_whatsapp_number_hidden === true
            ) && (
              <Button className="btn-sm mx-2" onClick={() => editLead(row)}>
                <i className="fa-regular fa-pen-to-square" title="Edit"></i>{" "}
                Edit
              </Button>
            )}
          </div>
        ),
      });
    }

    return cols;
    // user in deps so pin column shows after refresh when user loads; checkbox cells use refs for stability.
  }, [user]);
  const labels = {
    beforeSelect: " ",
  };

  const createLead = () => {
    navigate(`/leads/e`);
  };

  const handleImportLeads = () => {
    setShowModal(true);
  };

  const refreshData = () => {
    setShowModal(false);
    init();
  };

  // Function to clear all filters
  const clearAllFilters = () => {
    setSearchQuery("");
    setStatusFilter("");
    setSelectedTags([]);
    setSelectedAgentFilter([]);
  };

  // Function to remove a specific tag from selection
  const removeTag = (tagToRemove) => {
    setSelectedTags((prev) => prev.filter((tag) => tag.value !== tagToRemove));
  };

  // Custom styles for react-select
  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      borderColor: state.isFocused ? "#0d6efd" : "#dee2e6",
      "&:hover": {
        borderColor: "#0d6efd",
      },
      boxShadow: state.isFocused
        ? "0 0 0 0.25rem rgba(13, 110, 253, 0.25)"
        : "none",
      minHeight: "38px",
    }),
    multiValue: (base, state) => ({
      ...base,
      backgroundColor: state.data.color || "#e9ecef",
      borderRadius: "4px",
    }),
    multiValueLabel: (base, state) => ({
      ...base,
      color: state.data.color ? "#fff" : "#212529",
      fontWeight: "500",
    }),
    multiValueRemove: (base, state) => ({
      ...base,
      color: state.data.color ? "#fff" : "#6c757d",
      "&:hover": {
        backgroundColor: state.data.color ? "rgba(0,0,0,0.2)" : "#dee2e6",
        color: state.data.color ? "#fff" : "#dc3545",
      },
    }),
    menu: (base) => ({
      ...base,
      zIndex: 9999,
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999,
    }),
  };

  // Custom filter function for agents that searches across multiple fields
  const filterAgentOptions = (option, inputValue) => {
    const searchTerm = inputValue.toLowerCase().trim();
    if (!searchTerm) return true;

    const agent = option.data;
    return (
      agent.label?.toLowerCase().includes(searchTerm) ||
      agent.email?.toLowerCase().includes(searchTerm) ||
      agent.full_name?.toLowerCase().includes(searchTerm) ||
      agent.username?.toLowerCase().includes(searchTerm) ||
      false
    );
  };

  // Format agent labels with additional info
  const formatAgentLabel = (agent, { context }) => {
    if (context === "value") {
      return agent.label;
    }
    return (
      <div>
        <div className="fw-semibold">{agent.label}</div>
        {agent.email && <div className="small text-muted">{agent.email}</div>}
        {agent.full_name && agent.full_name !== agent.label && (
          <div className="small text-muted">{agent.full_name}</div>
        )}
      </div>
    );
  };

  return (
    <>
      <Container className="mt-5 mb-5">
        {/* Quick Guide Card */}
        <Row className="mx-5 mb-4">
          <Card className="p-4 bg-white shadow-sm border-0 rounded-3">
            <h5 className="mb-2 fw-bold">💡 Quick Guide</h5>
            <small>
              Manage your leads efficiently. Add tags to categorize leads and
              track their status. Use the search and filter options below to
              find leads quickly.
            </small>
          </Card>
        </Row>

        {/* Search & Filter Card */}
        <Row className="mx-5 mb-4">
          <Card className="p-4 bg-white shadow-sm border-0 rounded-3">
            <Row className="g-3">
              {/* Search Input */}
              <Col md={3}>
                <Form.Group>
                  <Form.Label className="small fw-semibold mb-1">
                    <FaSearch size={12} className="me-1" /> Search Leads
                  </Form.Label>
                  <Form.Control
                    type="text"
                    style={{ height: "36px" }}
                    placeholder="Search by name, email, phone, status, or agent..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="border-primary"
                  />
                </Form.Group>
              </Col>

              {/* Status Filter */}
              <Col md={2}>
                <Form.Group>
                  <Form.Label className="small fw-semibold mb-1">
                    <FaFilter size={12} className="me-1" /> Status
                  </Form.Label>
                  <Form.Select
                    aria-label="Filter by status"
                    value={statusFilter}
                    onChange={onFilterType}
                    style={{ height: "36px" }}
                    className="border-primary"
                  >
                    <option value="">All Status</option>
                    {leadStatusArray.map((item, index) => (
                      <option value={item.label} key={index}>
                        {item.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              {/* Tags Filter */}
              <Col md={3}>
                <Form.Group>
                  <Form.Label className="small fw-semibold mb-1">
                    <FaTag size={12} className="me-1" /> Filter by Tags
                  </Form.Label>
                  <Select
                    isMulti
                    options={availableTags}
                    value={selectedTags}
                    onChange={setSelectedTags}
                    placeholder="Select tags"
                    className="basic-multi-select"
                    classNamePrefix="select"
                    styles={customSelectStyles}
                    isSearchable
                    noOptionsMessage={() => "No tags found"}
                    closeMenuOnSelect={false}
                    blurInputOnSelect={false}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                </Form.Group>
              </Col>

              {/* Agent Filter */}
              <Col md={3}>
                <Form.Group>
                  <Form.Label className="small fw-semibold mb-1">
                    <FaUsers size={12} className="me-1" /> Filter by Agents
                  </Form.Label>
                  <Select
                    isMulti
                    options={availableAgents}
                    value={selectedAgentFilter}
                    onChange={setSelectedAgentFilter}
                    placeholder="Select agents"
                    className="basic-multi-select"
                    classNamePrefix="select"
                    styles={customSelectStyles}
                    isSearchable
                    noOptionsMessage={() => "No agents found"}
                    closeMenuOnSelect={false}
                    blurInputOnSelect={false}
                    filterOption={filterAgentOptions}
                    formatOptionLabel={formatAgentLabel}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                </Form.Group>
              </Col>

              {/* Clear Filters Button */}
              <Col md={1} className="d-flex align-items-end">
                <Button
                  variant="outline-secondary"
                  onClick={clearAllFilters}
                  className="py-2 mb-1"
                  title="Clear all filters"
                  disabled={
                    !searchQuery &&
                    !statusFilter &&
                    selectedTags.length === 0 &&
                    selectedAgentFilter.length === 0
                  }
                >
                  <FaTimes />
                </Button>
              </Col>
            </Row>
            <Row className="mt-2">
              <Col>
                <small className="text-muted">
                  <strong>Search includes:</strong> Name, Email, Phone, Status,
                  Agent names, Agent emails |<strong> Tag filter:</strong> OR
                  logic (shows leads with ANY selected tag) |
                  <strong> Agent filter:</strong> OR logic (shows leads assigned
                  to ANY selected agent)
                </small>
              </Col>
            </Row>
          </Card>
        </Row>

        {/* Leads Table Card */}
        <Row className="mx-5 g-0">
          <Col lg={12} className="mb-3">
            <Row className="g-0">
              <Col lg={12}>
                <div className="bg-white shadow-sm border-0 rounded-3 p-4">
                  <h5 className="fw-semibold mb-1">👥 All Leads</h5>

                  {isSpinner ? (
                    <LeadListSelectionContext.Provider
                      value={selectionContextValue}
                    >
                      <DatatableWrapper
                        body={body}
                        keyField="id"
                        headers={header}
                        paginationOptionsProps={{
                          initialState: {
                            rowsPerPage: 10,
                            options: [10, 20, 30, 50],
                          },
                        }}
                      >
                        <Row className="mb-3">
                          <Col
                            lg={3}
                            sm={10}
                            xs={10}
                            className="d-flex flex-col justify-content-end align-items-end"
                          >
                            <Filter className="p-2" />
                          </Col>
                          <Col
                            lg={4}
                            sm={2}
                            xs={2}
                            className="d-flex flex-col justify-content-start align-items-start"
                          >
                            <PaginationOptions labels={labels} />
                          </Col>
                          <Col
                            lg={5}
                            sm={12}
                            xs={12}
                            className="mt-2 d-flex flex-col justify-content-end align-items-end"
                          >
                            {/* Assign Agents Button - only shows when rows are selected */}
                            {userInfo?.userrole !== "USER" && (
                              <Button
                                className="btn-sm me-2"
                                variant="outline-success"
                                onClick={handleAssignAgentsClick}
                                disabled={selectedRows.length === 0 || loading}
                                title="Assign agents to selected leads"
                              >
                                <FaUserPlus size={12} className="me-1" />
                                Assign Agents ({selectedRows.length})
                              </Button>
                            )}

                            <Button
                              className="btn-sm me-2"
                              variant="outline-primary"
                              onClick={handleImportLeads}
                            >
                              <FaUpload size={12} className="me-1" /> Import
                              Leads
                            </Button>
                            <Button
                              className="btn-sm me-2"
                              variant="primary"
                              onClick={() => createLead(true)}
                            >
                              <FaPlus size={12} className="me-1" /> Add New Lead
                            </Button>
                            <Button
                              className="btn-sm"
                              variant="outline-danger"
                              disabled={selectedRows.length === 0 || loading}
                              onClick={async () => {
                                setShowConfirmDelete(true);
                              }}
                            >
                              Delete Selected ({selectedRows.length})
                            </Button>
                            <Button
                              className="btn-sm ms-2"
                              variant="outline-warning"
                              as={Link}
                              to="/leads/blocked"
                            >
                              <FaBan size={12} className="me-1" /> OTP Out /
                              Blocked
                            </Button>
                          </Col>
                        </Row>

                        <div className="table-responsive">
                          <Table
                            striped
                            bordered
                            className="related-list-table"
                            responsive="sm"
                          >
                            <TableHeader />
                            <TableBody />
                          </Table>
                        </div>
                        <Pagination />
                      </DatatableWrapper>
                    </LeadListSelectionContext.Provider>
                  ) : (
                    <ShimmerTable row={10} col={4} />
                  )}
                </div>
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>

      {/* Assign Agents Modal */}
      <Modal
        show={showAssignAgentsModal}
        onHide={handleCloseAssignModal}
        size="lg"
        centered
        backdrop="static"
      >
        <Modal.Header closeButton className="bg-light">
          <Modal.Title>
            <FaUsers className="me-2" />
            Assign Agents to Leads
          </Modal.Title>
        </Modal.Header>
        <Modal.Body
          className="p-4"
          style={{ maxHeight: "70vh", overflowY: "auto" }}
        >
          {/* Selected Leads Section with fixed height and scroll */}
          <div className="mb-4">
            <h6 className="fw-semibold mb-2">
              <FaUserFriends className="me-2" />
              Selected Leads ({selectedRows.length})
            </h6>
            <Card className="border">
              <Card.Body
                className="p-3"
                style={{
                  maxHeight: "200px",
                  overflowY: "auto",
                  minHeight: "100px",
                }}
              >
                {selectedLeads.length > 0 ? (
                  <ListGroup variant="flush">
                    {selectedLeads.map((lead) => (
                      <ListGroup.Item
                        key={lead.id}
                        className="d-flex justify-content-between align-items-start"
                        style={{ borderLeft: "none", borderRight: "none" }}
                      >
                        <div className="flex-grow-1">
                          <div className="d-flex justify-content-between align-items-start">
                            <div>
                              <strong className="d-block">
                                {lead.leadname}
                              </strong>
                              <div className="small text-muted">
                                {lead.email} • {lead.whatsapp_number}
                              </div>
                            </div>
                            <Button
                              variant="link"
                              size="sm"
                              className="text-danger p-0 ms-2"
                              onClick={() => removeSelectedLead(lead.id)}
                              title="Remove from selection"
                            >
                              <FaXmark size={16} />
                            </Button>
                          </div>
                          <Badge
                            bg={getStatusClass(lead.leadstatus)}
                            className="me-2 mt-2"
                          >
                            {lead.leadstatus}
                          </Badge>
                          {/* Show all current owners */}
                          {lead.all_owner_names &&
                            lead.all_owner_names.length > 0 && (
                              <div className="mt-2">
                                <small className="text-muted d-block mb-1">
                                  Current Owners:
                                </small>
                                <div className="d-flex flex-wrap gap-1">
                                  {lead.all_owner_names.map((owner, idx) => (
                                    <Badge
                                      key={idx}
                                      bg="info"
                                      className="me-1 mb-1"
                                    >
                                      {owner.name}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                        </div>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                ) : (
                  <div className="text-center text-muted py-3">
                    <FaUserFriends size={24} className="mb-2" />
                    <div>No leads selected</div>
                    <small>Select leads from the table first</small>
                  </div>
                )}
              </Card.Body>
            </Card>
          </div>

          {/* Assign Mode Selection */}
          <div className="mb-4">
            <h6 className="fw-semibold mb-2">Assignment Mode</h6>
            <Form>
              <Form.Check
                type="radio"
                id="replace-mode"
                label={
                  <>
                    <FaUserMinus className="me-2 text-danger" />
                    <strong>Replace Existing Agents</strong> - Remove all
                    current agents and assign only the selected agents
                  </>
                }
                name="assignMode"
                value="replace"
                checked={assignMode === "replace"}
                onChange={(e) => setAssignMode(e.target.value)}
                className="mb-2"
              />
              <Form.Check
                type="radio"
                id="add-mode"
                label={
                  <>
                    <FaUserPlus className="me-2 text-success" />
                    <strong>Add More Agents</strong> - Keep current agents and
                    add selected agents to them
                  </>
                }
                name="assignMode"
                value="add"
                checked={assignMode === "add"}
                onChange={(e) => setAssignMode(e.target.value)}
              />
            </Form>
          </div>

          {/* Agents Selection */}
          <div className="mb-4">
            <h6 className="fw-semibold mb-2">
              <FaUserCheck className="me-2" />
              Select Agents
            </h6>
            <Form.Group>
              <Select
                isMulti
                options={availableAgents}
                value={selectedAgents}
                onChange={setSelectedAgents}
                placeholder="Search and select agents (search by name, email, or username)..."
                className="basic-multi-select"
                classNamePrefix="select"
                styles={customSelectStyles}
                isSearchable
                noOptionsMessage={() => "No agents found"}
                closeMenuOnSelect={false}
                blurInputOnSelect={false}
                filterOption={filterAgentOptions}
                formatOptionLabel={formatAgentLabel}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
              <Form.Text className="text-muted">
                {assignMode === "replace"
                  ? "Selected agents will replace all current agents"
                  : "Selected agents will be added to existing agents"}
              </Form.Text>
            </Form.Group>
          </div>

          {/* Summary */}
          <Card className="border-info bg-info bg-opacity-10">
            <Card.Body className="p-3">
              <h6 className="fw-semibold">Summary</h6>
              <div className="small">
                <div>
                  • Leads to update: <strong>{selectedRows.length}</strong>
                </div>
                <div>
                  • Agents to assign: <strong>{selectedAgents.length}</strong>
                </div>
                <div>
                  • Mode:{" "}
                  <strong>
                    {assignMode === "replace" ? "Replace" : "Add"}
                  </strong>
                </div>
                <div>
                  • Agent names:{" "}
                  {selectedAgents.map((a) => a.label).join(", ") ||
                    "None selected"}
                </div>
              </div>
            </Card.Body>
          </Card>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={clearAllSelections}
            disabled={selectedRows.length === 0}
          >
            Clear Selections
          </Button>
          <Button variant="secondary" onClick={handleCloseAssignModal}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleAssignAgents}
            disabled={selectedAgents.length === 0 || assignLoading}
          >
            {assignLoading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Assigning...
              </>
            ) : (
              `Assign ${selectedAgents.length} Agent(s) to ${selectedRows.length} Lead(s)`
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modals */}
      {showModal && (
        <ImportLeads
          show={showModal}
          onHide={() => setShowModal(false)}
          refreshData={refreshData}
        />
      )}

      {showConfirmDelete && (
        <Confirm
          show={showConfirmDelete}
          onHide={() => setShowConfirmDelete(false)}
          deleteLead={async () => {
            setLoading(true);
            try {
              await WhatsAppAPI.bulkDeleteLeads({ ids: selectedRows });
              init();
              setData((prev) =>
                prev.filter((row) => !selectedRows.includes(row.id)),
              );
              setSelectedRows([]);
              setSelectedLeads([]);
              toast.success("Deleted successfully!");
            } catch (err) {
              console.error(err);
              toast.error("Failed to delete");
            } finally {
              setLoading(false);
              setShowConfirmDelete(false);
            }
          }}
          title="Confirm delete?"
          message={`You are going to delete ${selectedRows.length} record(s). Are you sure?`}
          table="lead"
        />
      )}
      <ToastContainer />
    </>
  );
};

export default LeadList;
